import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { initializeGekkoApi } from '../api/GekkoApi';

export interface SharedComponentsConfiguration {
  // API related
  gekkoApiBaseUrl: string;
  websocketUrl?: string;

  // Polling: when set, uses polling instead of WebSocket for real-time updates
  pollingIntervalMs?: number;

  // Environment
  environment?: 'development' | 'production' | 'test';
}

export interface ConfigurationContextValue {
  config: SharedComponentsConfiguration;
}

const ConfigurationContext = createContext<
  ConfigurationContextValue | undefined
>(undefined);

export interface SharedComponentsConfigurationProviderProps {
  children: ReactNode;
  config: SharedComponentsConfiguration;
}

export const SharedComponentsConfigurationProvider: React.FC<
  SharedComponentsConfigurationProviderProps
> = ({ children, config }) => {
  // Validate required configuration
  if (!config.gekkoApiBaseUrl) {
    throw new Error(
      'SharedComponentsConfigurationProvider: gekkoApiBaseUrl is required'
    );
  }

  // Initialize the GekkoApi instance with the provided configuration
  useEffect(() => {
    initializeGekkoApi(config);
  }, [config]);

  const value: ConfigurationContextValue = {
    config,
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};

export const useConfiguration = (): ConfigurationContextValue => {
  const context = useContext(ConfigurationContext);

  if (!context) {
    throw new Error(
      'useConfiguration must be used within a SharedComponentsConfigurationProvider'
    );
  }

  return context;
};

export default ConfigurationContext;
